import { pick, shiftDecimal } from '@silo/utils';
import { mapValues } from 'lodash';
import { CollateralAssetMetrics, DebtAssetMetrics, BaseLTV } from './types';
import {
  LiquidationPriceLegacy,
  NATIVE_CURRENCY_DECIMALS,
  PERCENT_DECIMALS,
  PRECISION_DECIMALS,
  RoundingType,
} from '@silo/types';

export const cropLTV = (ltv: BaseLTV): BaseLTV => {
  const collateralAssets = ltv.collateralAssets.map(cropCollateralAssetMetrics);
  const debtAssets = ltv.debtAssets.map(cropDebtAssetMetrics);

  return {
    ...mapValues(
      pick(ltv, [
        'userLTV',
        'userMaximumLTV',
        'userLiquidationThreshold',
        'healthFactor',
        'riskFactor',
        'borrowedPct',
        'remainingToBorrowPct',
      ]),
      cropPercentage
    ),
    ...mapValues(
      pick(ltv, [
        'collateralValue',
        'liquidationThresholdValue',
        'availableToBorrowValue',
        'borrowedValue',
        'remainingToBorrowValue',
        'liquidationBufferValue',
      ]),
      cropValue
    ),
    collateralAssets,
    debtAssets,
  };
};

const cropCollateralAssetMetrics = ({
  assetAddress,
  decimals,
  maxWithdrawTokens: { type, value },
  liquidationPrice,
}: CollateralAssetMetrics): CollateralAssetMetrics => {
  const cropTokens = getCropTokens(decimals);

  return {
    assetAddress,
    decimals,
    maxWithdrawTokens: {
      type,
      value: cropTokens(value),
    },
    liquidationPrice: cropLiquidationPrice(liquidationPrice),
  };
};

const cropDebtAssetMetrics = (asset: DebtAssetMetrics): DebtAssetMetrics => {
  const { assetAddress, decimals, liquidationPrice } = asset;
  const cropTokens = getCropTokens(decimals);

  return {
    assetAddress,
    decimals,
    ...mapValues(pick(asset, ['borrowedTokens', 'availableToBorrowTokens']), cropTokens),
    // rounding down on remaining to borrow so we don't put users in a compromising health factor
    remainingToBorrowTokens: getCropTokens(decimals, 'down')(asset.remainingToBorrowTokens),
    liquidationPrice: cropLiquidationPrice(liquidationPrice),
  };
};

const cropLiquidationPrice = (liquidationPrice: LiquidationPriceLegacy): LiquidationPriceLegacy => {
  if (liquidationPrice._tag === 'notApplicable') return liquidationPrice;

  return {
    _tag: 'applicable',
    value: cropValue(liquidationPrice.value),
  };
};

export const cropPercentage = (percentage: bigint): bigint =>
  shiftDecimal(percentage, PERCENT_DECIMALS - PRECISION_DECIMALS);

const cropValue = (value: bigint): bigint => shiftDecimal(value, NATIVE_CURRENCY_DECIMALS - PRECISION_DECIMALS);

export const getCropTokens =
  (decimals: number, roundingType?: RoundingType) =>
  (value: bigint): bigint =>
    shiftDecimal(value, decimals - PRECISION_DECIMALS, roundingType);
